/**
 * Create a signature for an employee.
 *
 * Three steps down the left, with the rendered signature sticky on the right. The BU comes first
 * because it decides where the record lands and whether the BFF will accept it at all — the
 * employee fields stay disabled until one is chosen.
 *
 * The preview is not decoration: it is the element captured with `toPng` and sent as the
 * signature image, so what the person sees is literally what is delivered.
 *
 * **One template for every BU.** Water and energy share the same signature today; the BU only
 * decides the destination of the record.
 *
 * @module Pages/Signatures
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { sendSignature } from '../../Services';
import { splitTenantsBySignatureAccess } from '../../Utils/signatureAccess';
import { BU_CONFIGS } from './SignatureCreate.types';
import { useTenants } from '@/Hooks/useTenants';
import { compareTenantSlugs, getTenantColor, getTenantIcon } from '@/Shared/brand';
import { useActionToast } from '@/Hooks/useActionToast';
import { SignaturePreview } from '../SignaturePreview/SignaturePreview';
import { formatBrazilianPhone } from '../../Utils/phone';
import { renderSignatureBase64 } from '../../Utils/downloadSignature';

const layoutStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xl);
  align-items: flex-start;
`;

const formColumnStyle = css`
  flex: 1 1 420px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
`;

const introStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-base);
`;

const descriptionStyle = css`
  margin: 0;
  max-width: 520px;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const spacerStyle = css`
  flex: 1;
`;

const stepStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
`;

const stepHeaderStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
`;

const stepNumberStyle = css`
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
  border-radius: var(--radius-round);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  background: var(--color-surface-sunken);
  color: var(--color-text-muted);
`;

const stepNumberActiveStyle = css`
  background: var(--color-primary);
  color: var(--color-on-primary);
`;

const stepTitleStyle = css`
  font-size: 14px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-muted);
`;

const stepTitleActiveStyle = css`
  color: var(--color-primary);
`;

/** Indents a step's body under its number. */
const stepBodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding-left: 34px;
`;

const chipsStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-s);
  padding-left: 34px;
`;

const chipStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-m);
  background: var(--color-surface);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

const chipSelectedStyle = css`
  border-color: var(--color-primary);
  background: var(--color-primary-subtle);
  color: var(--color-primary);
`;

const reviewTableStyle = css`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  overflow: hidden;
`;

const reviewRowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-base);
  padding: 10px var(--space-m);
  border-bottom: 1px solid var(--color-divider);

  &:last-child {
    border-bottom: none;
  }
`;

const reviewLabelStyle = css`
  flex: 0 0 120px;
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const reviewValueStyle = css`
  min-width: 0;
  font-size: var(--font-size-sm);
  overflow: hidden;
  text-overflow: ellipsis;
`;

const submitRowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  flex-wrap: wrap;
`;

const hintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const errorStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-danger);
`;

/** `top` clears the 64px sticky page header plus a gutter. */
const asideStyle = css`
  flex: 0 1 420px;
  min-width: 300px;
  position: sticky;
  top: 100px;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
`;

/*
  The frame lives out here, on a wrapper: the signature itself draws none, because anything on it
  is captured into the PNG that goes in the e-mail.
*/
const previewFrameStyle = css`
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  overflow: hidden;
`;

const asideLabelStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const noteStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  padding: var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
  font-size: var(--font-size-2xs);
  line-height: var(--line-height-normal);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const TEMPLATE = BU_CONFIGS.group_link_one;

export interface SignatureCreateProps {
  onBack?: () => void;
  onSuccess?: () => void;
}

const SignatureCreate = ({ onBack, onSuccess }: SignatureCreateProps): JSX.Element => {
  const { tenants } = useTenants();

  const toast = useActionToast();
  const [tenantSlug, setTenantSlug] = useState('');
  const [name, setName] = useState('');
  const [position, setPosition] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enabledTenants = useMemo(
    () =>
      splitTenantsBySignatureAccess(tenants).enabled.sort((a, b) =>
        compareTenantSlugs(a.slug, b.slug),
      ),
    [tenants],
  );

  const hasTenant = tenantSlug.length > 0;
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit =
    hasTenant && name.trim().length > 0 && position.trim().length > 0 && isEmailValid;

  const missing: string[] = [];
  if (!hasTenant) missing.push('a business unit');
  if (!name.trim()) missing.push('o nome');
  if (!position.trim()) missing.push('o cargo');
  if (!isEmailValid) missing.push('um e-mail válido');

  const handleSubmit = async () => {
    setIsSending(true);
    setError(null);

    try {
      const element = document.getElementById('signature-preview-container');
      if (!element) throw new Error('Pré-visualização não encontrada.');

      // Shared with the resend path: two copies of this would let the same person receive
      // images rendered at different resolutions depending on which button produced them.
      const base64Image = await renderSignatureBase64(element);

      await sendSignature({
        email: email.trim(),
        nome: name.trim(),
        phone: phone.trim(),
        position: position.trim(),
        image: base64Image,
        buCategory: tenantSlug,
      });

      toast.success('Assinatura enviada', `Ela foi para ${email.trim()}.`);
      onSuccess?.();
    } catch (sendError) {
      setError(
        sendError instanceof Error
          ? sendError.message
          : 'Não foi possível criar a assinatura. Tente de novo.',
      );
      toast.failure('Não foi possível criar a assinatura', sendError);
    } finally {
      setIsSending(false);
    }
  };

  const reviewRows = [
    { label: 'Business unit', value: tenantSlug || '—' },
    { label: 'Nome', value: name || '—' },
    { label: 'Cargo', value: position || '—' },
    { label: 'Telefone', value: phone || '—' },
    { label: 'E-mail', value: email || '—' },
  ];

  return (
    <div className={layoutStyle}>
      <section className={formColumnStyle}>
        <div className={introStyle}>
          <p className={descriptionStyle}>
            Escolha a business unit e preencha os dados do funcionário. O modelo da assinatura é o
            mesmo para todas as BUs.
          </p>
          <div className={spacerStyle} />
          <UTButton
            fitContent
            variant='tertiary'
            size='small'
            text='Voltar'
            icon='Arrows.ArrowLeft'
            onClick={() => {
              onBack?.();
            }}
          />
        </div>

        <div className={stepStyle}>
          <div className={stepHeaderStyle}>
            <span className={`${stepNumberStyle} ${stepNumberActiveStyle}`}>1</span>
            <span className={`${stepTitleStyle} ${stepTitleActiveStyle}`}>Business unit</span>
          </div>
          <div className={chipsStyle}>
            {enabledTenants.map(tenant => (
              <button
                key={tenant.slug}
                type='button'
                className={`${chipStyle} ${tenantSlug === tenant.slug ? chipSelectedStyle : ''}`}
                onClick={() => {
                  setTenantSlug(tenant.slug);
                }}
              >
                <GLIcon
                  variant={getTenantIcon(tenant)}
                  size={16}
                  fill={getTenantColor(tenant.slug)}
                />
                <span>{tenant.name || tenant.slug}</span>
              </button>
            ))}
          </div>
        </div>

        <div className={stepStyle}>
          <div className={stepHeaderStyle}>
            <span className={`${stepNumberStyle} ${hasTenant ? stepNumberActiveStyle : ''}`}>
              2
            </span>
            <span className={`${stepTitleStyle} ${hasTenant ? stepTitleActiveStyle : ''}`}>
              Dados do funcionário
            </span>
          </div>
          <div className={stepBodyStyle}>
            <UTTextField
              id='signature-name'
              topLabel='Nome'
              required
              disabled={!hasTenant}
              value={name}
              placeholder='Marina Duarte'
              onChange={event => {
                setName(event.target.value);
              }}
            />
            <UTTextField
              id='signature-position'
              topLabel='Cargo'
              required
              disabled={!hasTenant}
              value={position}
              placeholder='Product Designer'
              onChange={event => {
                setPosition(event.target.value);
              }}
            />
            <UTTextField
              id='signature-phone'
              topLabel='Telefone'
              disabled={!hasTenant}
              value={phone}
              placeholder='(11) 99999-0000'
              /*
                Masked as the person types: the number ends up inside an e-mail signature, read by
                people and parsed by nothing, and a bare 11916610024 there reads as a typo.
              */
              onChange={event => {
                setPhone(formatBrazilianPhone(event.target.value));
              }}
            />
            <UTTextField
              id='signature-email'
              topLabel='E-mail'
              required
              disabled={!hasTenant}
              value={email}
              placeholder='nome@grouplinkone.com'
              state={email.length > 0 && !isEmailValid ? 'error' : 'normal'}
              bottomLabel={
                email.length > 0 && !isEmailValid
                  ? 'Informe um e-mail válido.'
                  : 'A assinatura é enviada para este endereço.'
              }
              onChange={event => {
                setEmail(event.target.value);
              }}
            />
          </div>
        </div>

        <div className={stepStyle}>
          <div className={stepHeaderStyle}>
            <span className={`${stepNumberStyle} ${canSubmit ? stepNumberActiveStyle : ''}`}>
              3
            </span>
            <span className={`${stepTitleStyle} ${canSubmit ? stepTitleActiveStyle : ''}`}>
              Revisar e enviar
            </span>
          </div>
          <div className={stepBodyStyle}>
            <div className={reviewTableStyle}>
              {reviewRows.map(row => (
                <div key={row.label} className={reviewRowStyle}>
                  <span className={reviewLabelStyle}>{row.label}</span>
                  <span
                    className={reviewValueStyle}
                    style={row.value === '—' ? { color: 'var(--color-text-muted)' } : undefined}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>

            {error ? <span className={errorStyle}>{error}</span> : null}

            <div className={submitRowStyle}>
              <UTButton
                fitContent
                id='signature-submit'
                text={isSending ? 'Enviando...' : 'Criar e enviar assinatura'}
                icon='Arrows.Plus'
                disabled={!canSubmit || isSending}
                onClick={() => {
                  void handleSubmit();
                }}
              />
              <span className={hintStyle}>
                {canSubmit
                  ? 'A assinatura é enviada por e-mail ao funcionário.'
                  : `Informe ${missing.join(', ')}.`}
              </span>
            </div>
          </div>
        </div>
      </section>

      <aside className={asideStyle}>
        <span className={asideLabelStyle}>Pré-visualização · {TEMPLATE.displayName}</span>

        {/* Captured by toPng on submit — this element IS the delivered image. */}
        <div className={previewFrameStyle}>
          <SignaturePreview
            withPlaceholders
            id='signature-preview-container'
            name={name}
            position={position}
            phone={phone}
            email={email}
          />
        </div>

        <div className={noteStyle}>
          <GLIcon variant='Notifications.Info' size={16} fill='var(--color-text-muted)' />
          <span>
            Modelo único para todas as BUs. A business unit define apenas onde o registro da
            assinatura fica guardado.
          </span>
        </div>
      </aside>
    </div>
  );
};

export { SignatureCreate };
